import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from '../firebase';
function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

export default function MonthlyView() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const [data, setData] = useState({}); // { 'YYYY-MM-DD': { locationId: record } }
  const [locations, setLocations] = useState([]);
  const [workersMap, setWorkersMap] = useState({}); // { locationId: [{ name, phone }] }
  const [loadingLocs, setLoadingLocs] = useState(true);
  const [loading, setLoading] = useState(true);

  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
  const totalDays = daysInMonth(year, month);
  const days = Array.from({ length: totalDays }, (_, i) => i + 1);

  const MONTH_NAMES = ['January','February','March','April','May','June',
    'July','August','September','October','November','December'];

  const fetchLocations = async () => {
    setLoadingLocs(true);
    try {
      // Fetch active locations
      const snap = await getDocs(collection(db, 'locations'));
      const locList = [];
      snap.forEach(doc => {
        const data = doc.data();
        if (data.active !== false) {
          locList.push({ 
            id: doc.id, 
            locationName: data.locationName || doc.id,
            priority: data.priority || ''
          });
        }
      });
      locList.sort((a, b) => {
        const numA = parseInt((a.id || '').replace(/\D/g, '') || 0);
        const numB = parseInt((b.id || '').replace(/\D/g, '') || 0);
        return numA - numB;
      });
      setLocations(locList);

      // Fetch workers and build mapping
      const workersSnap = await getDocs(collection(db, 'workers'));
      const mapping = {};
      workersSnap.forEach(doc => {
        const d = doc.data();
        if (d.active !== false) {
          const wName = d.name || '';
          const wPhone = d.phone || '';
          const wLocs = d.locations || [];
          wLocs.forEach(locId => {
            const key = locId.toUpperCase().trim();
            if (!mapping[key]) {
              mapping[key] = [];
            }
            mapping[key].push({ name: wName, phone: wPhone });
          });
        }
      });
      setWorkersMap(mapping);
    } catch (e) {
      console.error('Failed to fetch locations or workers:', e);
    }
    setLoadingLocs(false);
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    async function fetchMonth() {
      setLoading(true);
      const result = {};
      for (let d = 1; d <= totalDays; d++) {
        const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
        try {
          const snap = await getDocs(collection(db, 'attendance', dateStr, 'locations'));
          if (!snap.empty) {
            result[dateStr] = {};
            snap.forEach(doc => { result[dateStr][doc.id] = doc.data(); });
          }
        } catch {}
      }
      setData(result);
      setLoading(false);
    }
    fetchMonth();
  }, [year, month, totalDays]);

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };

  const exportExcel = () => {
    const rows = [];
    // Header
    rows.push(['Location ID', 'Location Name', 'Priority', 'Assigned Picker', 'Picker Phone', ...days.map(d => `${d}/${month+1}`),'Total ✅', 'Total ❌']);

    for (const loc of locations) {
      const locId = loc.id;
      const locName = loc.locationName;
      const locPriority = loc.priority || '—';
      
      const assigned = workersMap[locId] || [];
      const pickerNames = assigned.map(w => w.name).filter(Boolean).join(', ') || '—';
      const pickerPhones = assigned.map(w => w.phone).filter(Boolean).join(', ') || '—';

      let done = 0;
      const row = [locId, locName, locPriority, pickerNames, pickerPhones];
      for (let d = 1; d <= totalDays; d++) {
        const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
        const rec = data[dateStr]?.[locId];
        if (rec) { row.push('✅'); done++; }
        else { row.push('❌'); }
      }
      row.push(done);
      row.push(totalDays - done);
      rows.push(row);
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [
      { wch: 12 }, // Location ID
      { wch: 26 }, // Location Name
      { wch: 10 }, // Priority
      { wch: 22 }, // Assigned Picker
      { wch: 18 }, // Picker Phone
      ...days.map(() => ({ wch: 6 })), 
      { wch: 10 }, 
      { wch: 10 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${MONTH_NAMES[month]} ${year}`);
    XLSX.writeFile(wb, `Ecoholic_Attendance_${monthStr}.xlsx`);
  };

  const isProgress = loading || loadingLocs;

  if (isProgress) return <div className="loading"><div className="spinner"></div>Loading monthly data...</div>;

  return (
    <>
      <div className="month-controls">
        <button className="month-btn" onClick={prevMonth}>‹</button>
        <span className="month-label">{MONTH_NAMES[month]} {year}</span>
        <button className="month-btn" onClick={nextMonth}>›</button>
        <button className="export-btn" onClick={exportExcel}>📥 Export Excel</button>
      </div>

      <div className="monthly-table-wrap">
        <table className="monthly-table">
          <thead>
            <tr>
              <th>Location</th>
              {days.map(d => (
                <th key={d}>{d}</th>
              ))}
              <th>✅</th>
              <th>❌</th>
            </tr>
          </thead>
          <tbody>
            {locations.map(loc => {
              let done = 0;
              const locId = loc.id;
              const locName = loc.locationName;
              const assigned = workersMap[locId] || [];
              const pickerNames = assigned.map(w => w.name).filter(Boolean).join(', ') || '—';
              const pickerPhones = assigned.map(w => w.phone).filter(Boolean).join(', ') || '—';
              return (
                <tr key={locId}>
                  <td title={locName}>
                    <strong>{locId}</strong><br/>
                    <small style={{color:'#64748b', display:'block', marginBottom:4}}>{locName}</small>
                    {pickerNames !== '—' && (
                      <small style={{color:'#34d399', fontSize:11, display:'block'}}>
                        👤 {pickerNames} ({pickerPhones})
                      </small>
                    )}
                  </td>
                  {days.map(d => {
                    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                    const rec = data[dateStr]?.[locId];
                    // Future dates
                    const isFuture = new Date(dateStr) > new Date(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }));
                    if (isFuture) return <td key={d} className="cell-na">—</td>;
                    if (rec) { done++; return <td key={d} className="cell-done" title={`${rec.workerName} at ${rec.submittedTime}`}>✅</td>; }
                    return <td key={d} className="cell-miss">✗</td>;
                  })}
                  <td style={{fontWeight:700, color:'#34d399', textAlign:'center'}}>{done}</td>
                  <td style={{fontWeight:700, color:'#f87171', textAlign:'center'}}>{totalDays - done}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
